import React, { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Search,
  FlaskConical,
  SlidersHorizontal,
  X,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Folder,
  Eraser,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/* Normalize text for case- and accent-insensitive search */
const normalize = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/* ============================================================
 * FilterPanel — shared between desktop sidebar and mobile sheet
 * ============================================================ */
function FilterPanel({
  areasDiagnosticas,
  especies,
  aplicaciones,
  selectedAreas,
  selectedEspecies,
  selectedApps,
  toggleArea,
  toggleEspecie,
  toggleApp,
  clearFilters,
  hasActiveFilters,
  areaCounts,
  especieCounts,
  appCounts,
}) {
  const [appSearch, setAppSearch] = useState('');
  const visibleApps = useMemo(() => {
    if (!appSearch.trim()) return aplicaciones;
    const t = normalize(appSearch);
    return aplicaciones.filter(a => normalize(a.nombre).includes(t));
  }, [aplicaciones, appSearch]);

  return (
    <div className="space-y-6">
      {/* Header with clear */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-200">
        <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-[#505BB3]" />
          Filtros
        </h2>
        <button
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className="text-xs text-[#505BB3] font-medium hover:underline disabled:text-slate-300 disabled:cursor-not-allowed disabled:no-underline flex items-center gap-1"
          data-testid="sidebar-clear-filters"
        >
          <Eraser className="w-3.5 h-3.5" />
          Limpiar todo
        </button>
      </div>

      {/* Área Diagnóstica */}
      {areasDiagnosticas.length > 0 && (
        <FilterSection
          icon={<Folder className="w-3.5 h-3.5 text-slate-400" />}
          title="Área Diagnóstica"
        >
          <ul className="space-y-1">
            {areasDiagnosticas.map(area => {
              const checked = selectedAreas.includes(area.id);
              const count = areaCounts[area.id] || 0;
              return (
                <FilterRow
                  key={area.id}
                  label={area.nombre}
                  count={count}
                  checked={checked}
                  onToggle={() => toggleArea(area.id)}
                  testId={`sb-area-${area.id}`}
                />
              );
            })}
          </ul>
        </FilterSection>
      )}

      {/* Especie */}
      {especies.length > 0 && (
        <FilterSection
          icon={<FlaskConical className="w-3.5 h-3.5 text-slate-400" />}
          title="Especie"
        >
          <ul className="space-y-1">
            {especies.map(esp => {
              const checked = selectedEspecies.includes(esp.id);
              const count = especieCounts[esp.id] || 0;
              return (
                <FilterRow
                  key={esp.id}
                  label={esp.nombre}
                  count={count}
                  checked={checked}
                  onToggle={() => toggleEspecie(esp.id)}
                  testId={`sb-especie-${esp.id}`}
                  accent="emerald"
                />
              );
            })}
          </ul>
        </FilterSection>
      )}

      {/* Aplicación Clínica */}
      {aplicaciones.length > 0 && (
        <FilterSection
          icon={<SlidersHorizontal className="w-3.5 h-3.5 text-slate-400" />}
          title="Aplicación Clínica"
        >
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={appSearch}
              onChange={(e) => setAppSearch(e.target.value)}
              placeholder="Buscar aplicación..."
              className="w-full h-8 pl-8 pr-7 text-xs rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#505BB3]/30 focus:border-[#505BB3]"
              data-testid="sidebar-app-search"
            />
            {appSearch && (
              <button
                onClick={() => setAppSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <ul className="space-y-1 max-h-72 overflow-y-auto pr-1 -mr-1">
            {visibleApps.length === 0 ? (
              <li className="text-xs text-slate-400 px-1 py-2 text-center">
                No hay aplicaciones que coincidan
              </li>
            ) : (
              visibleApps.map(app => {
                const checked = selectedApps.includes(app.id);
                const count = appCounts[app.id] || 0;
                return (
                  <FilterRow
                    key={app.id}
                    label={app.nombre}
                    count={count}
                    checked={checked}
                    onToggle={() => toggleApp(app.id)}
                    testId={`sb-app-${app.id}`}
                  />
                );
              })
            )}
          </ul>
        </FilterSection>
      )}
    </div>
  );
}

function FilterSection({ icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}

function FilterRow({ label, count, checked, onToggle, testId, accent = 'indigo' }) {
  const accentColor = accent === 'emerald'
    ? 'border-emerald-500 bg-emerald-500'
    : 'border-[#505BB3] bg-[#505BB3]';
  // Disable when count=0 AND not currently selected (always allow deselecting)
  const disabled = count === 0 && !checked;
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        aria-disabled={disabled}
        className={`w-full group flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
          disabled
            ? 'opacity-40 cursor-not-allowed'
            : checked
              ? 'bg-[#505BB3]/5'
              : 'hover:bg-slate-50'
        }`}
        data-testid={testId}
      >
        <span
          aria-hidden="true"
          className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
            checked ? `${accentColor} border-transparent` : 'border-slate-300 bg-white group-hover:border-slate-400'
          }`}
        >
          {checked && (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span className={`text-xs flex-1 truncate ${checked ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>
          {label}
        </span>
        <span
          className={`text-[10px] font-medium tabular-nums shrink-0 ${
            count === 0 ? 'text-slate-300' : 'text-slate-400'
          }`}
        >
          {count}
        </span>
      </button>
    </li>
  );
}

/* ============================================================
 * Main content
 * ============================================================ */
function CatalogoContent({ embed = false }) {
  const [grupos, setGrupos] = useState([]);
  const [pruebas, setPruebas] = useState([]);
  const [aplicaciones, setAplicaciones] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [areasDiagnosticas, setAreasDiagnosticas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApps, setSelectedApps] = useState([]);
  const [selectedEspecies, setSelectedEspecies] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [expandedGrupos, setExpandedGrupos] = useState({});
  const [selectedPrueba, setSelectedPrueba] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gruposRes, pruebasRes, appsRes, espRes, areasRes] = await Promise.all([
        axios.get(`${API}/api/catalogo/grupos`),
        axios.get(`${API}/api/catalogo/pruebas`),
        axios.get(`${API}/api/catalogo/aplicaciones-clinicas`),
        axios.get(`${API}/api/catalogo/especies`),
        axios.get(`${API}/api/catalogo/areas-diagnosticas`),
      ]);
      setGrupos(gruposRes.data);
      setPruebas(pruebasRes.data);
      setAplicaciones(appsRes.data);
      setEspecies(espRes.data);
      setAreasDiagnosticas(areasRes.data);
      setExpandedGrupos({});
    } catch (error) {
      console.error('Error fetching catalog:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const appsMap = useMemo(() => Object.fromEntries(aplicaciones.map(a => [a.id, a])), [aplicaciones]);
  const especiesMap = useMemo(() => Object.fromEntries(especies.map(e => [e.id, e])), [especies]);
  const areasMap = useMemo(() => Object.fromEntries(areasDiagnosticas.map(a => [a.id, a])), [areasDiagnosticas]);
  const grupoToArea = useMemo(() => {
    const m = {};
    grupos.forEach(g => { m[g.id] = g.area_diagnostica_id; });
    return m;
  }, [grupos]);

  /* ─────────── Dynamic counters (facet-style, like Amazon/Booking) ─────────── *
   * For each option in section S, count = tests matching:
   *   - search term (if any)
   *   - filters from ALL OTHER sections (not section S)
   *   - having this specific option in section S
   * This way, counters reflect what the user would see if they toggled that option,
   * and stay consistent regardless of multi-select within the same section.
   */
  const matchesSearch = useMemo(() => {
    if (!searchTerm) return () => true;
    const terms = normalize(searchTerm).split(/\s+/).filter(Boolean);
    return (p) => {
      const haystack = `${normalize(p.nombre)} ${normalize(p.codigo)} ${normalize(p.descripcion || '')}`;
      return terms.every(term => haystack.includes(term));
    };
  }, [searchTerm]);

  const { areaCounts, especieCounts, appCounts } = useMemo(() => {
    const passSearch = matchesSearch;
    const passArea = (p) =>
      selectedAreas.length === 0 || selectedAreas.includes(grupoToArea[p.grupo_id]);
    const passEspecie = (p) =>
      selectedEspecies.length === 0 ||
      selectedEspecies.some(id => (p.especies || []).includes(id));
    const passApp = (p) =>
      selectedApps.length === 0 ||
      selectedApps.some(id => (p.aplicaciones_clinicas || []).includes(id));

    const aC = {}, eC = {}, pC = {};

    // Area section: apply all OTHER sections (search + especie + app), then count by area
    pruebas.forEach(p => {
      if (!passSearch(p) || !passEspecie(p) || !passApp(p)) return;
      const aId = grupoToArea[p.grupo_id];
      if (aId) aC[aId] = (aC[aId] || 0) + 1;
    });
    // Especie section: apply all OTHER sections (search + area + app), then count by each especie
    pruebas.forEach(p => {
      if (!passSearch(p) || !passArea(p) || !passApp(p)) return;
      (p.especies || []).forEach(id => { eC[id] = (eC[id] || 0) + 1; });
    });
    // App section: apply all OTHER sections (search + area + especie), then count by each app
    pruebas.forEach(p => {
      if (!passSearch(p) || !passArea(p) || !passEspecie(p)) return;
      (p.aplicaciones_clinicas || []).forEach(id => { pC[id] = (pC[id] || 0) + 1; });
    });
    return { areaCounts: aC, especieCounts: eC, appCounts: pC };
  }, [pruebas, grupoToArea, matchesSearch, selectedAreas, selectedEspecies, selectedApps]);

  /* ─────────── Filtered tests (same predicates as counters → consistency) ─────────── */
  const filteredPruebas = useMemo(() => {
    return pruebas.filter(p => {
      if (!matchesSearch(p)) return false;
      if (selectedAreas.length > 0 && !selectedAreas.includes(grupoToArea[p.grupo_id])) return false;
      if (selectedEspecies.length > 0 && !selectedEspecies.some(id => (p.especies || []).includes(id))) return false;
      if (selectedApps.length > 0 && !selectedApps.some(id => (p.aplicaciones_clinicas || []).includes(id))) return false;
      return true;
    });
  }, [pruebas, matchesSearch, selectedAreas, selectedEspecies, selectedApps, grupoToArea]);

  const gruposWithPruebas = useMemo(() => {
    return grupos
      .map(g => ({
        ...g,
        pruebas: filteredPruebas
          .filter(p => p.grupo_id === g.id)
          .sort((a, b) => (a.orden || 0) - (b.orden || 0)),
      }))
      .filter(g => g.pruebas.length > 0);
  }, [grupos, filteredPruebas]);

  const toggleApp = (id) =>
    setSelectedApps(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleEspecie = (id) =>
    setSelectedEspecies(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleArea = (id) =>
    setSelectedAreas(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedApps([]);
    setSelectedEspecies([]);
    setSelectedAreas([]);
  };

  const totalActiveFilters =
    selectedApps.length + selectedEspecies.length + selectedAreas.length + (searchTerm ? 1 : 0);
  const hasActiveFilters = totalActiveFilters > 0;

  const filterPanelProps = {
    areasDiagnosticas,
    especies,
    aplicaciones,
    selectedAreas,
    selectedEspecies,
    selectedApps,
    toggleArea,
    toggleEspecie,
    toggleApp,
    clearFilters,
    hasActiveFilters,
    areaCounts,
    especieCounts,
    appCounts,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[#505BB3] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Cargando catálogo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={embed ? '' : ''}>
      {/* Search bar — sticky */}
      <div className={`sticky top-0 z-30 ${embed ? 'bg-white pb-3' : 'bg-white/90 backdrop-blur-xl border-b border-slate-200/60 px-4 md:px-8 py-3'}`}>
        <div className={embed ? '' : 'max-w-7xl mx-auto'}>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar prueba por nombre, código o descripción..."
              className="pl-12 pr-12 h-11 md:h-12 text-sm md:text-base rounded-full border-slate-200 shadow-sm focus-visible:ring-2 focus-visible:ring-[#505BB3]/30 focus-visible:border-[#505BB3]"
              data-testid="catalog-search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body: sidebar + main */}
      <div className={embed ? 'mt-4' : 'max-w-7xl mx-auto px-4 md:px-8 py-6'}>
        <div className="flex gap-6">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-[80px] bg-white border border-slate-200 rounded-xl p-4 shadow-sm max-h-[calc(100vh-100px)] overflow-y-auto">
              <FilterPanel {...filterPanelProps} />
            </div>
          </aside>

          {/* Main column */}
          <main className="flex-1 min-w-0">
            {/* Active filter chips + count */}
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                <span className="text-sm text-slate-600" data-testid="catalog-results-count">
                  <span className="font-semibold text-slate-900">{filteredPruebas.length}</span> resultado{filteredPruebas.length !== 1 ? 's' : ''}
                </span>
                {hasActiveFilters && <span className="text-slate-300">·</span>}
                {selectedAreas.map(id => {
                  const a = areasMap[id];
                  return a ? (
                    <button
                      key={id}
                      onClick={() => toggleArea(id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-50 text-violet-700 border border-violet-200 text-[11px] font-medium rounded-full hover:bg-violet-100 transition-colors"
                    >
                      {a.nombre}
                      <X className="w-3 h-3" />
                    </button>
                  ) : null;
                })}
                {selectedEspecies.map(id => {
                  const e = especiesMap[id];
                  return e ? (
                    <button
                      key={id}
                      onClick={() => toggleEspecie(id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-medium rounded-full hover:bg-emerald-100 transition-colors"
                    >
                      {e.nombre}
                      <X className="w-3 h-3" />
                    </button>
                  ) : null;
                })}
                {selectedApps.map(id => {
                  const a = appsMap[id];
                  return a ? (
                    <button
                      key={id}
                      onClick={() => toggleApp(id)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#505BB3]/10 text-[#505BB3] border border-[#505BB3]/20 text-[11px] font-medium rounded-full hover:bg-[#505BB3]/15 transition-colors"
                    >
                      {a.nombre}
                      <X className="w-3 h-3" />
                    </button>
                  ) : null;
                })}
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-[#505BB3] font-medium hover:underline whitespace-nowrap"
                  data-testid="clear-filters-btn"
                >
                  Limpiar todo
                </button>
              )}
            </div>

            {/* Results */}
            {gruposWithPruebas.length === 0 ? (
              <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
                <FlaskConical className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No se encontraron pruebas</h3>
                <p className="text-sm text-slate-500 mt-1">Intenta con otros filtros o términos de búsqueda</p>
              </div>
            ) : (
              <div className="space-y-4">
                {gruposWithPruebas.map(grupo => (
                  <div key={grupo.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid={`catalog-grupo-${grupo.id}`}>
                    <button
                      onClick={() => setExpandedGrupos(prev => ({ ...prev, [grupo.id]: !prev[grupo.id] }))}
                      className="w-full flex items-center justify-between px-5 py-4 bg-slate-50/80 border-b border-slate-100 hover:bg-slate-100/80 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#505BB3]" />
                        <h3 className="font-semibold text-slate-900 text-sm md:text-base">{grupo.nombre}</h3>
                        <Badge variant="secondary" className="text-[11px] bg-slate-200/80 text-slate-600">
                          {grupo.pruebas.length}
                        </Badge>
                      </div>
                      {expandedGrupos[grupo.id] ? (
                        <ChevronUp className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      )}
                    </button>

                    {expandedGrupos[grupo.id] && (
                      <div className="bg-slate-50/50 p-4 md:p-5 grid grid-cols-1 gap-3">
                        {grupo.pruebas.map(prueba => (
                          <div
                            key={prueba.id}
                            className="group bg-white rounded-xl border border-slate-200 p-4 md:p-5 hover:border-[#505BB3]/40 hover:shadow-md shadow-sm cursor-pointer transition-all duration-200"
                            onClick={() => setSelectedPrueba(prueba)}
                            data-testid={`catalog-prueba-${prueba.id}`}
                          >
                            <div className="flex items-start justify-between gap-3 mb-1">
                              <h4 className="font-semibold text-slate-900 text-sm md:text-[15px] leading-snug flex-1">
                                {prueba.nombre}
                              </h4>
                              {prueba.precio > 0 && (
                                <span className="text-base md:text-lg font-bold text-emerald-600 whitespace-nowrap shrink-0">
                                  ${prueba.precio.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono mb-3">
                              Código: #{prueba.codigo}
                            </div>
                            {prueba.descripcion && (
                              <p className="text-xs text-slate-600 mb-3 leading-relaxed line-clamp-2">
                                {prueba.descripcion}
                              </p>
                            )}
                            {(prueba.especies || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-100">
                                {(prueba.especies || []).map(espId => {
                                  const esp = especiesMap[espId];
                                  return esp ? (
                                    <Badge key={espId} variant="secondary" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                                      {esp.nombre}
                                    </Badge>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Footer info */}
            {!embed && (
              <div className="text-center mt-12 pb-8">
                <p className="text-xs text-slate-400">
                  {pruebas.length} pruebas disponibles en {grupos.length} categorías
                </p>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile floating filter button */}
      <button
        onClick={() => setMobileFiltersOpen(true)}
        className="lg:hidden fixed bottom-5 right-5 z-40 flex items-center gap-2 px-5 h-12 rounded-full bg-[#505BB3] text-white shadow-lg shadow-[#505BB3]/40 font-medium text-sm hover:bg-[#404A9C] active:scale-95 transition-all"
        data-testid="mobile-filter-fab"
      >
        <SlidersHorizontal className="w-4 h-4" />
        Filtros
        {totalActiveFilters > 0 && (
          <span className="flex items-center justify-center min-w-[22px] h-5 px-1.5 bg-white text-[#505BB3] text-[11px] font-bold rounded-full">
            {totalActiveFilters}
          </span>
        )}
      </button>

      {/* Mobile sheet from bottom */}
      <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <SheetContent side="bottom" className="h-[88vh] rounded-t-2xl p-0 flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <SheetTitle className="text-base font-bold flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#505BB3]" />
              Filtros
            </SheetTitle>
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="text-slate-400 hover:text-slate-600 p-1 -m-1"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-5">
            <FilterPanel {...filterPanelProps} />
          </div>
          <div className="flex items-center gap-3 p-4 border-t border-slate-200 bg-white">
            <button
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="flex-1 h-11 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              data-testid="mobile-filter-clear"
            >
              Limpiar
            </button>
            <button
              onClick={() => setMobileFiltersOpen(false)}
              className="flex-[2] h-11 rounded-full bg-[#505BB3] text-white text-sm font-semibold hover:bg-[#404A9C]"
              data-testid="mobile-filter-apply"
            >
              Ver {filteredPruebas.length} resultado{filteredPruebas.length !== 1 ? 's' : ''}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detail Modal */}
      <Dialog open={!!selectedPrueba} onOpenChange={(open) => { if (!open) setSelectedPrueba(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          {selectedPrueba && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-slate-900 leading-snug pr-6">
                  {selectedPrueba.nombre}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500 font-mono">Código: #{selectedPrueba.codigo}</span>
                  {selectedPrueba.precio > 0 && (
                    <div className="flex items-center gap-1.5 bg-[#505BB3]/5 px-3 py-1.5 rounded-lg">
                      <DollarSign className="w-4 h-4 text-[#505BB3]" />
                      <span className="text-lg font-bold text-[#505BB3]">
                        {selectedPrueba.precio.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </span>
                      <span className="text-xs text-slate-500">MXN</span>
                    </div>
                  )}
                </div>

                {selectedPrueba.descripcion && (() => {
                  const desc = selectedPrueba.descripcion;
                  const isQpcrList = (desc.match(/qPCR\s*\(/gi) || []).length >= 2;
                  if (isQpcrList) {
                    const items = desc
                      .split(/(?:[,;\n]|\s+(?=qPCR\s*\())/i)
                      .map(item => item.trim())
                      .filter(item => item.length > 0);
                    return (
                      <div>
                        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Pruebas incluidas</h4>
                        <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                          <ul className="space-y-1.5">
                            {items.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                                <div className="w-1.5 h-1.5 rounded-full bg-[#505BB3] mt-1.5 shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div>
                      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Descripción</h4>
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                        <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  );
                })()}

                {selectedPrueba.tipo_muestra && selectedPrueba.tipo_muestra.trim() && (
                  <div data-testid="prueba-detail-tipo-muestra">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Tipo de muestra</h4>
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{selectedPrueba.tipo_muestra}</p>
                    </div>
                  </div>
                )}

                {selectedPrueba.especies && selectedPrueba.especies.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Especies</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedPrueba.especies.map(espId => {
                        const esp = especiesMap[espId];
                        return esp ? (
                          <Badge key={espId} className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                            {esp.nombre}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {selectedPrueba.aplicaciones_clinicas && selectedPrueba.aplicaciones_clinicas.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Aplicaciones Clínicas</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedPrueba.aplicaciones_clinicas.map(appId => {
                        const app = appsMap[appId];
                        return app ? (
                          <Badge key={appId} className="px-3 py-1 bg-indigo-50 text-indigo-600 border border-indigo-200 text-xs">
                            {app.nombre}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================
 * Page wrappers
 * ============================================================ */
export function CatalogoPublicoPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="bg-[#505BB3] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-7 md:py-10 text-center">
          <span className="text-white/60 text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em]">AmplicareLab</span>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mt-1.5 mb-2">
            Catálogo de Pruebas
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
            Explora nuestro catálogo completo de pruebas disponibles.
          </p>
        </div>
      </div>
      <CatalogoContent embed={false} />
    </div>
  );
}

export function CatalogoEmbedPage() {
  return (
    <div className="min-h-screen bg-white">
      <CatalogoContent embed={true} />
    </div>
  );
}

export default CatalogoPublicoPage;
